<template>
  <div class="multi-select-card" :class="cardClass">
    <!-- 标题栏 -->
    <div class="multi-select-card__titlebar">
      <span class="multi-select-card__titlebar-icon">
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none">
          <rect x="3" y="3" width="18" height="18" rx="3" stroke="currentColor" stroke-width="1.8"/>
          <path d="M8 12l3 3 5-5" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"/>
        </svg>
      </span>
      <span class="multi-select-card__titlebar-text">{{ card.question }}</span>
      <span class="multi-select-card__status-badge">{{ statusBadge }}</span>
    </div>

    <!-- 选项列表（pending 状态且本地未提交时显示） -->
    <div v-if="card.status === 'pending' && !localSubmitted" class="multi-select-card__options">
      <div
        v-for="opt in card.options"
        :key="opt.value"
        class="multi-select-card__option"
        :class="{ 'multi-select-card__option--selected': localSelected.includes(opt.value) }"
        @click="toggleOption(opt.value)"
      >
        <span class="multi-select-card__option-checkbox">
          <svg v-if="localSelected.includes(opt.value)" width="14" height="14" viewBox="0 0 24 24" fill="none">
            <rect x="2" y="2" width="20" height="20" rx="4" fill="#0052D9"/>
            <path d="M7 12l4 4 6-7" stroke="#fff" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
          </svg>
          <svg v-else width="14" height="14" viewBox="0 0 24 24" fill="none">
            <rect x="2" y="2" width="20" height="20" rx="4" stroke="#c9cdd4" stroke-width="1.8"/>
          </svg>
        </span>
        <span class="multi-select-card__option-body">
          <span class="multi-select-card__option-label">{{ opt.label }}</span>
          <span v-if="opt.description" class="multi-select-card__option-desc">{{ opt.description }}</span>
        </span>
      </div>

      <!-- 底部操作栏 -->
      <div class="multi-select-card__footer">
        <span class="multi-select-card__count-hint">{{ countHint }}</span>
        <div class="multi-select-card__footer-btns">
          <button class="multi-select-card__btn multi-select-card__btn--cancel" @click="onCancel">取消</button>
          <button
            class="multi-select-card__btn multi-select-card__btn--confirm"
            :disabled="!canConfirm || submitting"
            @click="onConfirm"
          >确认</button>
        </div>
      </div>
    </div>

    <!-- 已提交状态 -->
    <div v-else-if="card.status === 'answered' || localSubmitted" class="multi-select-card__answered">
      <span class="multi-select-card__answered-icon">✓</span>
      <span class="multi-select-card__answered-text">{{ answeredLabel }}</span>
    </div>

    <!-- 已过期 / 已取消 -->
    <div v-else class="multi-select-card__answered multi-select-card__answered--expired">
      <span class="multi-select-card__answered-text">已超时取消</span>
    </div>
  </div>
</template>

<script>
import { computed, ref } from '@vue/composition-api';

export default {
  name: 'MultiSelectOptionsCard',
  props: {
    card: {
      type: Object,
      required: true,
    },
  },
  emits: ['action'],
  setup(props, { emit }) {
    const localSelected = ref([]); // string[]
    const localSubmitted = ref(false);
    const submitting = ref(false);

    const cardClass = computed(() => {
      const effectiveStatus = localSubmitted.value ? 'answered' : props.card.status;
      return `multi-select-card--${effectiveStatus}`;
    });

    const statusBadge = computed(() => {
      const effectiveStatus = localSubmitted.value ? 'answered' : props.card.status;
      const map = { pending: '多选', answered: '已选择', expired: '已超时' };
      return map[effectiveStatus] || '';
    });

    const minSelect = computed(() => props.card.minSelect ?? 1);
    const maxSelect = computed(() => props.card.maxSelect ?? Infinity);

    const canConfirm = computed(
      () => localSelected.value.length >= minSelect.value && localSelected.value.length <= maxSelect.value,
    );

    const countHint = computed(() => {
      const cnt = localSelected.value.length;
      const min = minSelect.value;
      const max = props.card.maxSelect;
      if (max) return `已选 ${cnt} / 最多 ${max} 项（至少 ${min} 项）`;
      return `已选 ${cnt} 项（至少 ${min} 项）`;
    });

    const answeredLabel = computed(() => {
      // 优先用本地选中的
      const values = localSubmitted.value
        ? localSelected.value
        : (props.card.selectedValues || []);
      if (!values.length) return '';
      const opts = props.card.options || [];
      return values
        .map((v) => {
          const opt = opts.find((o) => o.value === v);
          return opt ? opt.label : v;
        })
        .join('、');
    });

    function toggleOption(value) {
      const idx = localSelected.value.indexOf(value);
      if (idx !== -1) {
        localSelected.value.splice(idx, 1);
      } else {
        if (localSelected.value.length < maxSelect.value) {
          localSelected.value.push(value);
        }
      }
    }

    function onConfirm() {
      if (!canConfirm.value || submitting.value) return;
      submitting.value = true;
      localSubmitted.value = true;
      // value 用 JSON 序列化传递多值
      emit('action', props.card.cardId, 'multi-select', JSON.stringify(localSelected.value));
    }

    function onCancel() {
      emit('action', props.card.cardId, 'cancel', undefined);
    }

    return {
      localSelected,
      localSubmitted,
      submitting,
      cardClass,
      statusBadge,
      canConfirm,
      countHint,
      answeredLabel,
      toggleOption,
      onConfirm,
      onCancel,
    };
  },
};
</script>

<style scoped>
.multi-select-card {
  background: #fff;
  border-radius: 12px;
  border: 1.5px solid #d8e3f5;
  overflow: hidden;
  margin: 4px 0;
  box-shadow: 0 2px 8px rgba(0, 82, 217, 0.08);
  width: 100%;
}
.multi-select-card--answered { border-color: #b7ebc8; box-shadow: 0 2px 8px rgba(7, 193, 96, 0.08); }
.multi-select-card--expired { border-color: #e8ecf0; box-shadow: none; opacity: 0.75; }

.multi-select-card__titlebar {
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 8px 14px;
  background: linear-gradient(135deg, #f0f4ff 0%, #e8f0fe 100%);
  border-bottom: 1px solid #d8e3f5;
}
.multi-select-card__titlebar-icon {
  display: flex;
  align-items: center;
  color: #0052D9;
  flex-shrink: 0;
}
.multi-select-card__titlebar-text {
  font-size: 13px;
  font-weight: 600;
  color: #1a202c;
  flex: 1;
  line-height: 1.4;
  word-break: break-word;
}
.multi-select-card__status-badge {
  font-size: 11px;
  padding: 2px 7px;
  border-radius: 999px;
  background: #e8f0fe;
  color: #0052D9;
  font-weight: 500;
  white-space: nowrap;
}
.multi-select-card--answered .multi-select-card__status-badge { background: #e8f8ef; color: #07c160; }
.multi-select-card--expired .multi-select-card__status-badge { background: #f5f7fa; color: #86909c; }

.multi-select-card__options {
  display: flex;
  flex-direction: column;
  gap: 4px;
  padding: 8px 14px 0;
}
.multi-select-card__option {
  display: flex;
  align-items: flex-start;
  gap: 8px;
  padding: 8px 10px;
  border-radius: 8px;
  border: 1.5px solid #e2e8f0;
  background: #f8faff;
  cursor: pointer;
  transition: all 0.15s ease;
  user-select: none;
}
.multi-select-card__option:hover {
  border-color: #0052D9;
  background: #f0f4ff;
}
.multi-select-card__option--selected {
  border-color: #0052D9;
  background: #f0f4ff;
}
.multi-select-card__option-checkbox {
  display: flex;
  align-items: center;
  flex-shrink: 0;
  margin-top: 1px;
}
.multi-select-card__option-body {
  display: flex;
  flex-direction: column;
  gap: 2px;
  min-width: 0;
}
.multi-select-card__option-label { font-size: 13px; font-weight: 500; color: #1a202c; }
.multi-select-card__option-desc { font-size: 12px; color: #86909c; }

.multi-select-card__footer {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 8px 0 12px;
  margin-top: 4px;
}
.multi-select-card__count-hint {
  font-size: 12px;
  color: #86909c;
}
.multi-select-card__footer-btns {
  display: flex;
  gap: 8px;
}
.multi-select-card__btn {
  padding: 5px 14px;
  border-radius: 6px;
  font-size: 13px;
  font-weight: 500;
  cursor: pointer;
  border: none;
  transition: all 0.15s ease;
}
.multi-select-card__btn--cancel {
  background: #f5f7fa;
  color: #4a5568;
  border: 1px solid #e2e8f0;
}
.multi-select-card__btn--cancel:hover { background: #e8ecf0; }
.multi-select-card__btn--confirm {
  background: #0052D9;
  color: #fff;
}
.multi-select-card__btn--confirm:hover:not(:disabled) { background: #2979FF; }
.multi-select-card__btn--confirm:disabled { opacity: 0.45; cursor: not-allowed; }

.multi-select-card__answered {
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 10px 14px 12px;
}
.multi-select-card__answered--expired { opacity: 0.6; }
.multi-select-card__answered-icon { font-size: 14px; color: #07c160; }
.multi-select-card__answered-text { font-size: 13px; color: #4a5568; }
</style>
